<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light'
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header -->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo & Text -->
          <li class="nav-item mr-auto">
            <b-link class="navbar-brand" to="/">
              <span class="brand-logo">
                <b-img class="menu_logo" :src="menu_logo" alt="logo" />
              </span>
              <h2 class="brand-text">Safetravel.today</h2>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / main menu header -->

    <!-- Shadow -->
    <div :class="{ 'd-block': shallShadowBottom }" class="shadow-bottom" />

    <!-- main menu content -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="ul"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <vertical-nav-menu-items
        :items="menuAdmin"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- /main menu content -->
  </div>
</template>

<script>
import { ref, computed, provide } from '@vue/composition-api'
import { BLink, BImg } from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'
import useAppConfig from '@core/app-config/useAppConfig'
import navMenuItemsImport from '@/navigation/vertical'
import store from '@/store'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: { type: Boolean, required: true },
    toggleVerticalMenuActive: { type: Function, required: true },
  },
  setup(props) {
    // --- Menu state (collapse, hover, toggler)
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    // --- App skin
    const { skin } = useAppConfig()

    // --- Shadow bottom
    const shallShadowBottom = ref(false)

    // --- Logo
    const menu_logo = require('@/assets/images/quotation/slogo.svg')

    // --- Nav items and user role
    const ls_role = ref(store?.state?.app?.userData?.role ?? 'client')
    const navMenuItems = ref(navMenuItemsImport)

    // --- Filtered menu (reactive computed)
    const menuAdmin = computed(() => {
      if (ls_role.value === 'admin') return navMenuItems.value
      const restrictedItems = ['Admin Panel','menu.AdminPanel']
      return navMenuItems.value.filter(item => !restrictedItems.includes(item.title))
    })

    // --- Perfect scrollbar settings
    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    // --- Collapse icon
    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'
    )

    // --- Provide hover state for child components
    provide('isMouseHovered', isMouseHovered)

    return {
      // UI & state
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
      collapseTogglerIconFeather,
      shallShadowBottom,
      skin,
      perfectScrollbarSettings,
      menu_logo,

      // Nav
      navMenuItems,
      menuAdmin,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.menu_logo {
  width: 100%;
  max-width: 50px;
}
</style>
